import * as Yup from 'yup';
import { useMemo } from 'react';
import { enqueueSnackbar } from 'notistack';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';

import { DatePicker } from '@mui/x-date-pickers';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';

import { fCurrency, fCurrencyRupees } from 'src/utils/format-number';

import { useAppDispatcher } from 'src/redux/store';
import { createChequeSettlement, updateChequeSettlement } from 'src/redux/slices/cheque';

import FormProvider, { RHFTextField } from 'src/components/hook-form';

import { IChequeItem, IChequeSettlement } from 'src/types/cheque';

interface ISettlementDialog {
  cheque: IChequeItem;
  // Present when editing an existing company settlement.
  settlement?: IChequeSettlement;
  isOpen: boolean;
  onClose: VoidFunction;
  onSuccess: VoidFunction;
  // Still owed to the supplier, excluding the settlement being edited.
  outstandingAmount: number;
}

function SettlementDialog({
  cheque,
  settlement,
  isOpen,
  onClose,
  onSuccess,
  outstandingAmount,
}: ISettlementDialog) {
  const dispatch = useAppDispatcher();
  const isEdit = !!settlement;

  const schema = useMemo(
    () =>
      Yup.object().shape({
        settlementDate: Yup.date().required('Settlement date is required'),
        settlementAmount: Yup.number()
          .required('Settlement amount is required')
          .min(0.01, 'Settlement amount must be at least 0.01')
          .max(
            outstandingAmount / 100,
            `Cannot exceed the amount still owed to the supplier (${fCurrency(
              outstandingAmount / 100
            )})`
          ),
        note: Yup.string().optional(),
      }),
    [outstandingAmount]
  );

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      settlementDate: settlement ? new Date(settlement.settlementDate) : new Date(),
      settlementAmount: (settlement?.amountInCents ?? outstandingAmount) / 100,
      note: settlement?.note ?? '',
    },
    mode: 'onChange',
  });

  const {
    handleSubmit,
    control,
    formState: { isSubmitting },
    reset,
  } = methods;

  const onSubmit = handleSubmit(async (data) => {
    const body = {
      settlementDate: new Date(data.settlementDate),
      amountInCents: Math.round(data.settlementAmount * 100),
      note: data.note || undefined,
    };

    try {
      if (settlement) {
        await dispatch(
          updateChequeSettlement({
            chequeId: cheque._id,
            settlementId: settlement._id,
            settlement: body,
          })
        ).unwrap();
        enqueueSnackbar('Settlement updated successfully');
      } else {
        await dispatch(createChequeSettlement({ chequeId: cheque._id, settlement: body })).unwrap();
        enqueueSnackbar('Settlement recorded successfully');
      }

      reset();
      onSuccess();
      onClose();
    } catch (e) {
      enqueueSnackbar(typeof e === 'string' ? e : e?.message || 'Failed to save settlement', {
        variant: 'error',
      });
    }
  });

  return (
    <Dialog fullWidth maxWidth="xs" open={isOpen} onClose={onClose}>
      <DialogTitle sx={{ pb: 2 }}>{isEdit ? 'Edit Settlement' : 'Settle Supplier'}</DialogTitle>
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <DialogContent sx={{ typography: 'body2' }}>
          <Typography sx={{ mb: 2 }} variant="body2">
            Record a payment the company made to the supplier for this returned cheque. Still owed:{' '}
            <strong>{fCurrencyRupees(outstandingAmount)}</strong>
          </Typography>

          <Stack spacing={2}>
            <Controller
              name="settlementDate"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <DatePicker
                  label="Settlement Date"
                  disableFuture
                  value={field.value}
                  onChange={(newValue) => field.onChange(newValue)}
                  slotProps={{
                    textField: { fullWidth: true, error: !!error, helperText: error?.message },
                  }}
                />
              )}
            />

            <RHFTextField
              name="settlementAmount"
              label="Settlement Amount (Rupees)"
              type="number"
              required
            />

            <RHFTextField
              name="note"
              label="Note (Optional)"
              multiline
              rows={3}
              placeholder="e.g., Bank transfer ref, cash, etc."
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" color="inherit" onClick={onClose}>
            Cancel
          </Button>
          <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
            {isEdit ? 'Save' : 'Record Settlement'}
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}

export default SettlementDialog;
